/**
 * صفحه‌ی یک سیگنال — قرینه‌ی apps/mobile/app/signal/[id].tsx.
 *
 * تقسیمِ دیدنی‌ها عیناً همونِ موبایله، و پشتوانه‌ش RLS است نه این کد:
 *  - گیرنده: فقط جوابِ **خودش** رو می‌بینه.
 *  - سازنده: فهرستِ کاملِ جواب‌ها + دکمه‌ی قطعی کردن.
 * اگه این فایل هم اشتباه بنویسه، دیتابیس بازم ردیف‌ها رو نمی‌ده.
 */
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { signalRepo } from '@/core/data/signalRepo';
import { inviteRepo } from '@/core/data/inviteRepo';
import { notificationRepo } from '@/core/data/miscRepo';
import { friendRepo } from '@/core/data/socialRepo';
import { inviteUrl } from '@/core/lib/shareLink';
import { useSession } from '@/core/session';
import { kindMeta } from '@/core/domain/signalKinds';
import { t, useLocale } from '@/core/lib/i18n';
import { formatWeekdayDate, signalWhenLabel } from '@/core/lib/dates';
import { composeFinalStart, confirmMissingAll, defaultFinalWhen } from '@/core/lib/planConfirm';
import type { FinalWhen } from '@/core/lib/planConfirm';
import { rosterWorthShowing } from '@/core/lib/planRoster';
import { buildIcs, googleCalendarUrl } from '@/core/lib/calendar';
import { REMINDER_OPTIONS } from '@/core/lib/reminder';
import { formatApprox } from '@/core/lib/approxLabel';
import { answerLabel } from '@/core/lib/answerLabel';
import { answeredOnOldVersion } from '@/core/lib/signalDiff';
import { isExternalInvite, responseChipLabel } from '@/core/lib/recipientRow';
import { signalUrl } from '@/core/lib/shareLink';
import { describeError, reportError } from '@/core/lib/report';
import { handleFilterBlocked } from '@/ui/filterBlocked';
import { trackEventOpen, trackFunnel } from '@/core/lib/analytics';
import { PushOfferCard } from '@/features/notifications/PushOfferCard';
import { ReportButton } from '@/features/report/ReportMenu';
import { Card, Note } from '@/ui/kit';
import { CancelledIcon } from '@/ui/icons';
import { EventDetails } from './EventDetails';
import { HeldSignalCard } from './HeldSignalCard';
import { HeldMessageNote } from './HeldMessageNote';
import { RespondMenu } from './RespondMenu';
import { SignalPhotos } from './SignalPhotos';

type Tab = 'details' | 'chat' | 'photos';

/** فقط چیزی که خودمان می‌فرستیم؛ هر چیزِ دیگری همان «جزئیات» است. */
const tabFromParam = (v: string | null): Tab => (v === 'photos' || v === 'chat' ? v : 'details');

/** پیامِ هر فیلدِ کم — ترتیبش همان ترتیبِ فرم است. */
const GAP_KEY: Record<'day' | 'clock' | 'place', string> = {
  day: 'confirmNeedsDate', clock: 'confirmNeedsTime', place: 'confirmNeedsPlace',
};

export function SignalPage() {
  useLocale();
  const { id } = useParams<{ id: string }>();
  const { profile } = useSession();
  const qc = useQueryClient();
  const nav = useNavigate();
  // کلیکِ یادآورِ عکس یک‌راست همان سربرگ را باز می‌کند (مایگریشن 0141)؛
  // فقط مقدارِ اولیه است، بعدش دستِ خودِ کاربر.
  const [search] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => tabFromParam(search.get('tab')));
  const [msg, setMsg] = useState('');
  const [copied, setCopied] = useState(false);
  /**
   * فرمِ ساخت با «یک لینک هم بساز» لینک رو کپی کرده و با state رسیده این‌جا
   * (CreateSignalPage). یه «کپی شد» بالای دکمه‌ی اشتراک همون خبریه که شیتِ
   * اشتراکِ موبایل خودش می‌داد. یک‌بار موقعِ mount خونده می‌شه.
   */
  const location = useLocation();
  const [arrivedWithCopiedLink] = useState(
    !!(location.state as { linkCopied?: boolean } | null)?.linkCopied,
  );
  /** توکنی که همین الان کپی شد — تا فقط همان دکمه «کپی شد» بگوید. */
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const copyInvite = async (token: string) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(token));
      setCopiedToken(token);
      trackFunnel('share', { id: id!, source: 'signal', method: 'copy', link: 'invite' });
    } catch { /* کلیپ‌بورد اجازه نداد */ }
  };

  const signal = useQuery({
    queryKey: ['signal', id],
    queryFn: () => signalRepo.byId(id!, profile!.id),
    enabled: !!id && !!profile,
  });
  const isCreator = signal.data?.creatorId === profile?.id;

  const recipients = useQuery({
    queryKey: ['recipients', id],
    queryFn: () => signalRepo.recipients(id!),
    enabled: !!id && isCreator,
  });
  /** لینک‌های شخصیِ دعوت‌شده‌های بی‌حساب. فقط سازنده می‌بیندشون (RLS). */
  const invites = useQuery({
    queryKey: ['invites', id],
    queryFn: () => inviteRepo.forSignal(id!),
    enabled: !!id && isCreator,
  });
  /** دعوتِ زنجیره‌ای (مایگریشن 0132): فهرستِ دوستان تا خواسته نشده باز نمی‌شود. */
  const [chainOpen, setChainOpen] = useState(false);
  const [chainError, setChainError] = useState<string | null>(null);
  const chainFriends = useQuery({
    queryKey: ['friends'],
    queryFn: () => friendRepo.list(profile!.id),
    enabled: chainOpen && !!profile,
  });
  const chain = useMutation({
    mutationFn: (fid: string) => signalRepo.chainInvite(id!, [fid]),
    onSuccess: () => {
      trackFunnel('invite_sent', { id: id!, target: 'friend', count: 1 });
      setChainError(null);
      qc.invalidateQueries({ queryKey: ['signal', id] });
    },
    onError: (e) => {
      // خطای supabase-js شیءِ ساده است، نه Error — پس instanceof این‌جا
      // همیشه رد می‌شد و پیامِ اختصاصی هیچ‌وقت نشان داده نمی‌شد.
      const msg = describeError(e);
      if (msg.includes('chain limit reached')) { setChainError(t('chainLimitReached')); return; }
      reportError('web.signal.chainInvite', e);
      setChainError(t('errorRetry'));
    },
  });

  /** «۴ از ۶» و «n نفرِ دیگر» — شمارِ «هستم» برای همه‌ی مخاطبان (0131). */
  const inCount = useQuery({
    queryKey: ['inCount', id],
    queryFn: () => signalRepo.inCount(id!),
    enabled: !!id && !!profile
      && !!(signal.data?.capacityMax || signal.data?.quorumMin),
  });

  /** بازدیدِ لینکِ عمومی — فقط سازنده (RLS جدولِ signal_link_stats). */
  const linkViews = useQuery({
    queryKey: ['linkViews', id],
    queryFn: () => inviteRepo.linkViews(id!),
    enabled: !!id && isCreator,
  });
  const resend = useMutation({
    mutationFn: (inviteId: number) => inviteRepo.resendEmail(inviteId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invites', id] }),
    onError: (e) => reportError('web.signal.resendInvite', e),
  });

  // قیف (WHOSUP-291): یک بار به ازای هر سیگنال، وقتی داده رسید.
  const loadedId = signal.data?.id;
  useEffect(() => { if (loadedId) trackEventOpen(loadedId, 'signal'); }, [loadedId]);

  // باز کردنِ صفحه = «دیدم» (WHOSUP-136). فقط برای گیرنده معنا دارد.
  useEffect(() => {
    if (signal.data && !isCreator) signalRepo.markSeen(signal.data.id);
  }, [signal.data?.id, isCreator]);   // eslint-disable-line react-hooks/exhaustive-deps
  const plan = useQuery({
    queryKey: ['plan', id],
    queryFn: () => signalRepo.plan(id!),
    enabled: !!id,
  });
  /**
   * «چه کسانی هستند» — فقط بعد از قطعی شدن و فقط برای سازنده و «هستم»ها
   * (WHOSUP-267، مایگریشن 0155). بقیه اصلاً کوئری نمی‌زنند؛ دیتابیس هم
   * برایشان خالی برمی‌گرداند.
   */
  const roster = useQuery({
    queryKey: ['planRoster', id],
    queryFn: () => signalRepo.planRoster(id!),
    enabled: !!id && !!plan.data && (isCreator || signal.data?.myResponse === 'in'),
  });
  const messages = useQuery({
    queryKey: ['messages', id],
    queryFn: () => signalRepo.messages(id!),
    enabled: !!id,
  });

  // پیامِ تازه‌ی طرفِ مقابل بدونِ رفرش بیاد — همون اشتراکِ Realtime موبایل.
  useEffect(() => {
    if (!id) return;
    return signalRepo.subscribeMessages(id, () => {
      qc.invalidateQueries({ queryKey: ['messages', id] });
    });
  }, [id, qc]);

  /**
   * باز بودنِ سربرگِ گفتگو/عکس‌ها = «این را دیدم» (مایگریشن 0148).
   *
   * قرینه‌ی همین اثر در apps/mobile/app/signal/[id].tsx است و هر دو لازمند:
   * تریگرِ دیتابیس تا وقتی یک خبرِ نخوانده‌ی همین برنامه هست، خبرِ تازه‌ای
   * نمی‌سازد. پس اگر این‌جا خوانده نشود، پیامِ دوم به بعد هیچ اعلانی ندارد.
   *
   * وابستگی به `messages.data` عمدی است: کسی که همین حالا داخلِ گفتگوست و
   * پیامِ تازه برایش می‌آید، آن را روی صفحه **دیده** — خبرش نباید نخوانده
   * بماند و درِ خبرِ بعدی را ببندد.
   */
  useEffect(() => {
    if (!id || !profile || (tab !== 'chat' && tab !== 'photos')) return;
    notificationRepo.markSignalTabSeen(profile.id, id, tab)
      .catch((e) => reportError('web.signal.markTabSeen', e));
  }, [id, profile?.id, tab, messages.data]);   // eslint-disable-line react-hooks/exhaustive-deps

  const sendMsg = useMutation({
    mutationFn: () => signalRepo.sendMessage(id!, profile!.id, msg.trim()),
    onSuccess: () => { setMsg(''); qc.invalidateQueries({ queryKey: ['messages', id] }); },
    onError: (e) => { if (handleFilterBlocked(e)) return; reportError('web.signal.sendMessage', e); },
  });

  /**
   * زمان و مکانِ نهایی، با انتخابِ کاربر.
   *
   * قبلاً این‌جا `Date.now() + 24h` بود: سیگنالِ «شنبه ۸ شب» که پنجشنبه
   * ساعت ۳ قطعی می‌شد، برنامه‌ای برای جمعه ساعت ۳ ثبت می‌کرد. حالا پیش‌فرض
   * از خودِ سیگنال می‌آید و سیگنالی که اصلاً زمان ندارد (`coordinate`،
   * `approx`) بدونِ پر کردنِ تاریخ اصلاً قطعی نمی‌شود.
   *
   * `null` یعنی پنل هنوز باز نشده.
   */
  const [finalWhen, setFinalWhen] = useState<FinalWhen | null>(null);
  const [finalPlace, setFinalPlace] = useState('');
  /**
   * چه چیزی کم است — ولی فقط **بعد از** زدنِ دکمه.
   *
   * دکمه عمداً خاکستری نمی‌شود: دکمه‌ی غیرفعال علتش را نمی‌گوید و کاربر
   * باید حدس بزند کدام فیلد ناقص است. حالا فعال می‌مانَد و با زدنش کاملِ
   * فهرست را می‌گوید. پیش از اولین تلاش هم چیزی نشان نمی‌دهد، وگرنه فرم
   * از همان لحظه‌ی باز شدن سرزنش می‌کند.
   */
  const [confirmGaps, setConfirmGaps] = useState<string[]>([]);
  /** یادآورِ انتخابی، دقیقه. صفر یعنی «بدون یادآور». پیش‌فرض همان ۱۲۰ی دیتابیس. */
  const [remind, setRemind] = useState(120);

  const openConfirm = () => {
    const sig = signal.data;
    if (!sig) return;
    setConfirmGaps([]);
    setFinalWhen(defaultFinalWhen(sig));
    // سیگنالی که از رخدادِ Discover زاده شده اغلب `placeName` ندارد و محلش
    // در خودِ رخداد است. حالا که مکان اجباری است، نگرفتنِ آن یعنی کاربر
    // چیزی را دوباره تایپ کند که اپ از قبل می‌داند.
    setFinalPlace(sig.placeName ?? sig.event?.venue ?? '');
  };

  const confirm = useMutation({
    mutationFn: () => {
      const start = finalWhen && composeFinalStart(finalWhen);
      // دکمه بدونِ تاریخ غیرفعاله؛ این فقط نگهبانِ آخره.
      if (!start) throw new Error('final start missing');
      return signalRepo.confirmPlan(id!, start, finalPlace.trim() || undefined, remind);
    },
    onSuccess: () => {
      setFinalWhen(null);
      qc.invalidateQueries({ queryKey: ['plan', id] });
      qc.invalidateQueries({ queryKey: ['signal', id] });
    },
    onError: (e) => reportError('web.signal.confirmPlan', e),
  });

  const outcome = useMutation({
    mutationFn: (a: { o: 'done' | 'cancelled' | 'partial'; share: 'none' | 'friends' }) =>
      signalRepo.setOutcome(id!, a.o, a.share),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plan', id] }),
    onError: (e) => reportError('web.signal.setOutcome', e),
  });

  /**
   * پس گرفتنِ سیگنال. لغو یا حذف بودنش تصمیمِ دیتابیسه (`remove_signal`)، و
   * همون تصمیم می‌گه بعدش کجا بریم: چیزی که حذف شده صفحه‌ای نداره.
   */
  const remove = useMutation({
    mutationFn: () => signalRepo.remove(id!),
    onSuccess: (what) => {
      qc.invalidateQueries({ queryKey: ['mine'] });
      qc.invalidateQueries({ queryKey: ['incoming'] });
      if (what === 'deleted') nav('/signals', { replace: true });
      else qc.invalidateQueries({ queryKey: ['signal', id] });
    },
    onError: (e) => reportError('web.signal.remove', e),
  });
  /** یه بار پرسیدن، بعد انجام دادن — بدونِ دیالوگِ مرورگر وسطِ کار. */
  const [confirmRemove, setConfirmRemove] = useState(false);

  if (signal.isLoading) return <div className="feed"><p className="dim">…</p></div>;
  if (!signal.data) {
    /*
     * برنامه‌ای که این حساب نمی‌بیند (مالِ کسِ دیگر، یا پاک‌شده). بدونِ دکمه
     * این صفحه بن‌بست بود (WHOSUP-359): اگر از لینک یا بعد از ورود رسیده
     * باشی، زیرش در تاریخچه چیزی نیست — پس آن‌وقت به «برنامه‌های من».
     */
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    return (
      <div className="feed">
        <Card>
          <p className="dim">{t('error')}</p>
          <button type="button" className="btn" data-testid="signal-missing-back"
            onClick={() => (idx > 0 ? nav(-1) : nav('/plans', { replace: true }))}>
            {t('back')}
          </button>
        </Card>
      </div>
    );
  }

  const s = signal.data;
  const meta = kindMeta(s.kind);
  const cancelled = s.status === 'cancelled';
  const canChat = (isCreator || s.myResponse === 'in') && !cancelled;
  /** جواب دادم، بعد سازنده عوضش کرد — نیمه‌ی درون‌صفحه‌ایِ «الارم». */
  const staleAnswer = answeredOnOldVersion(s);

  /**
   * روی وب «اشتراک» یعنی کپیِ لینک.
   * `navigator.share` فقط روی گوشی و در بسترِ امن هست؛ روی دسکتاپ اصلاً وجود
   * نداره. پس کپی مسیرِ اصلیه و share اگه بود، بهتره.
   */
  const share = async () => {
    const url = signalUrl(s.shareToken);
    try {
      const sheet = typeof navigator.share === 'function';
      const method = sheet ? 'sheet' : 'copy';
      if (sheet) await navigator.share({ title: s.title, url });
      else await navigator.clipboard.writeText(url);
      setCopied(true);
      trackFunnel('share', { id: s.id, source: 'signal', method, link: 'public' });
    } catch { /* کاربر بست یا کلیپ‌بورد اجازه نداد */ }
  };

  return (
    <div className="feed">
      {/* WHOSUP-342: «بازگشت» به همان صفحه‌ای که از آن آمده (مثلاً «برنامه‌های من»)؛
          لینکِ مستقیم که تاریخچه‌ی داخلِ سایت ندارد به «برنامه‌های من» می‌رود، نه «/» (کشف).
          تازه‌ساخته هم به «برنامه‌های من»: صفحه‌ی قبل از فرم می‌تواند برنامه‌ی قبلی باشد. */}
      <Link to="/plans" className="dim"
        onClick={(e) => {
          if (location.key === 'default' || (location.state as { created?: boolean } | null)?.created) return;
          e.preventDefault();
          nav(-1);
        }}>
        {t('back')}
      </Link>
      <h1 className="feed-title">
        {cancelled ? <CancelledIcon size={18} className="cancel-mark" /> : null}
        {s.title}
      </h1>
      <p className="dim">{s.creator?.displayName ?? ''}</p>

      {/* «عکس‌ها» همیشه دیده می‌شود ولی تا قطعی نشدنِ برنامه قفل است —
          سربرگی که غیب می‌شود، کاربر فکر می‌کند اپ خراب است (WHOSUP-213). */}
      <div className="chiprow">
        <button type="button" className={`chip${tab === 'details' ? ' on' : ''}`}
          onClick={() => setTab('details')}>{t('tabDetails')}</button>
        <button type="button" className={`chip${tab === 'chat' ? ' on' : ''}`}
          onClick={() => setTab('chat')}>{t('tabChat')}</button>
        <button type="button" className={`chip${tab === 'photos' ? ' on' : ''}`}
          onClick={() => setTab('photos')}>{t('tabPhotos')}</button>
      </div>

      {tab === 'details' ? (
        <>
          {/* لغو بالای همه‌چیز: جواب دادن، چت و قطعی کردن همه با «این دیگه
              سرِ جاش نیست» بی‌معنی می‌شن. */}
          {cancelled ? (
            <Card><Note tone="brand">
              {t('signalCancelledBadge')} — {t('signalCancelledNote')}
            </Note></Card>
          ) : null}

          {/* نگه‌داشته (0174): بالای همه، چون هیچ‌کس هنوز دعوتش را نگرفته. */}
          {isCreator && s.status === 'held' ? <HeldSignalCard signal={s} /> : null}

          {staleAnswer && !cancelled ? (
            <Card><Note tone="brand">
              ⏰ {t('changedSinceYourAnswer')} — {t('changedSinceYourAnswerHint')}
            </Note></Card>
          ) : null}

          {!isCreator && !cancelled ? (
            <Card>
              <h3>{t('yourAnswer')}</h3>
              <p>{answerLabel(s)}</p>
              <RespondMenu signal={s} />
              <Note>{t('answerPrivateNote')}</Note>
            </Card>
          ) : null}

          {/* دعوتِ زنجیره‌ای (مایگریشن 0132): «هستم» گفته‌ای و سازنده اجازه
              داده — دوستِ خودت را بیاور. سقف و بقیه‌ی قواعد کارِ دیتابیس‌اند. */}
          {!isCreator && !cancelled && s.chainLimit !== 0 && s.myResponse === 'in' ? (
            <Card>
              {chainOpen ? (
                <>
                  <h3>{t('chainInviteBtn')}</h3>
                  {chainError ? <Note tone="warn">{chainError}</Note> : null}
                  {(chainFriends.data ?? [])
                    .filter((f) => f.status === 'accepted')
                    .map((f) => (
                      <div key={f.profile.id} className="tally-row">
                        <span>{f.profile.displayName}</span>
                        <button type="button" className="btn" disabled={chain.isPending}
                          onClick={() => chain.mutate(f.profile.id)}>
                          {t('chainInviteBtn')}
                        </button>
                      </div>
                    ))}
                </>
              ) : (
                <button type="button" className="btn" onClick={() => setChainOpen(true)}>
                  {t('chainInviteBtn')}
                </button>
              )}
            </Card>
          ) : null}

          <Card>
            <h3>{meta.emoji} {meta.badge(s)}</h3>
            <p className="dim">
              {s.approxLabel ? formatApprox(s.approxLabel)
                : s.startsAt ? signalWhenLabel(s) : t('coordinate')}
            </p>
            {s.placeName ? <p>{s.placeName}</p> : null}
            {/* سیگنالی که از صفحه‌ی کشف زاده شده، مکان و آدرسش این‌جاست —
                `placeName` در آن حالت خالیه و تا امروز هیچ‌جا نشون داده
                نمی‌شد، حتی به کاربرِ حساب‌دار. */}
            <EventDetails event={s.event} />
            {s.costNote ? <p className="dim">{s.costNote}</p> : null}
            {s.note ? <p>{s.note}</p> : null}
            {/* ظرفیت/نصاب/مهلت — برای همه‌ی مخاطبان (مایگریشن 0131). */}
            {s.capacityMax ? (
              <p className="dim">
                {t('capacityOf', { n: String(inCount.data ?? 0), max: String(s.capacityMax) })}
              </p>
            ) : null}
            {s.quorumMin && s.status === 'active' && (inCount.data ?? 0) < s.quorumMin ? (
              <p className="dim">{t('quorumNeed', { n: String(s.quorumMin - (inCount.data ?? 0)) })}</p>
            ) : null}
            {s.rsvpDeadline && s.status === 'active'
              && new Date(s.rsvpDeadline).getTime() > Date.now() ? (
                <p className="dim">⏳ {t('respondBy', { when: formatWeekdayDate(s.rsvpDeadline) })}</p>
              ) : null}
            {/* «کپی شد» خالی نامفهوم بود — چی کپی شد؟ متنِ کامل: لینکِ
                عمومیه، بفرست برای هر کسی، ولی اون صفحه جواب نمی‌گیره
                (مایگریشن 0067) — جواب مستقیم به خودت می‌رسه. tone سبزِ
                تاییدیه تا از بقیه‌ی صفحه متمایز باشه (خواسته‌ی ریویو). */}
            {arrivedWithCopiedLink
              ? <Note tone="ok">{t('publicLinkCopiedNote')}</Note> : null}
            <div className="chiprow">
              <button type="button" className="btn" onClick={share}>
                {copied ? t('doneCheck') : t('shareLink')}
              </button>
              {/* تاریخچه برای همه‌ست، نه فقط سازنده: کسی که جواب داده باید
                  بتونه ببینه از اون‌موقع چی عوض شده. تا چیزی عوض نشده
                  (نسخه‌ی ۱) دکمه‌ای هم لازم نیست. */}
              {s.version > 1 ? (
                <Link className="btn" to={`/signal/${s.id}/versions`}>{t('versionHistory')}</Link>
              ) : null}
              {/* گزارشِ خودِ سیگنال (WHOSUP-168) — فقط برای کسی که سازنده‌اش
                  نیست: گزارشِ چیزی که خودم نوشته‌ام کاری برای کسی نمی‌سازد،
                  و دیتابیس هم برای پروفایل همین را با قیدِ `not_self` می‌گوید. */}
              {!isCreator ? <ReportButton target={{ kind: 'signal', id: s.id }} /> : null}
            </div>
          </Card>

          {isCreator && !cancelled ? (
            <Card>
              <div className="chiprow">
                <Link className="btn" to={`/signal/${s.id}/edit`}>{t('editSignal')}</Link>
                <button type="button" className="btn" disabled={remove.isPending}
                  onClick={() => setConfirmRemove(true)}>
                  {t('removeSignal')}
                </button>
              </div>
              {confirmRemove ? (
                <>
                  {/* یکی از دو متن، نه هر دو: وقتی هیچ‌کس نگفته «هستم»،
                      حرف زدن از الارم فقط یه هشدارِ بی‌ربطه. `counts` برای
                      سازنده کاملِ فهرسته (RLS). */}
                  <Note>{(s.counts?.in ?? 0) > 0
                    ? t('removeSignalConfirmCancel') : t('removeSignalConfirmDelete')}</Note>
                  <div className="chiprow">
                    <button type="button" className="btn fill" disabled={remove.isPending}
                      onClick={() => remove.mutate()}>
                      {remove.isPending ? '…' : t('removeSignalConfirmYes')}
                    </button>
                    <button type="button" className="btn"
                      onClick={() => setConfirmRemove(false)}>
                      {t('contactsPrivacyCancel')}
                    </button>
                  </div>
                </>
              ) : null}
            </Card>
          ) : null}

          {/* فقط سازنده. برای بقیه این کوئری اصلاً اجرا نمی‌شه. */}
          {isCreator ? (
            <Card>
              <h3>{t('answersOnlyYou')}</h3>
              {(recipients.data ?? []).map((r) => {
                const inv = (invites.data ?? []).find((i) => i.recipientRowId === r.id);
                /*
                 * وضعیتِ هر دعوت، ماندگار (WHOSUP-136):
                 *   • پیامکِ گروهی لینکِ عمومی گرفته که جواب نمی‌گیرد — پس
                 *     «منتظرِ پاسخ» دروغ است؛ دسته‌ی جدا می‌گیرد.
                 *   • ایمیل: نرفت (خطا + تلاشِ دوباره) / رفت / باز شد.
                 *   • کاربرِ اپِ بی‌جواب: دیده یا هنوز ندیده (seen_at).
                 */
                const external = isExternalInvite(r);
                const delivery = inv?.error ? 'failed'
                  : inv?.openedAt ? 'opened'
                    : inv?.sentAt ? 'sent' : null;
                return (
                  <div key={r.id} className="tally-row">
                    {/* دعوت‌شده‌ی بی‌حساب پروفایل نداره؛ اسمِ دفترچه یا خودِ
                        آدرس تنها چیزیه که ازش داریم (مایگریشن 0066/0067). */}
                    <span>
                      {r.profile?.displayName ?? r.invitedName
                        ?? r.invitedEmail ?? r.invitedPhone ?? t('guest')}
                      {/* لینکِ شخصی رو بردار و از هر راهی که خودت می‌خوای
                          بفرست — واتساپ، تلگرام، هرچی. */}
                      {inv ? (
                        <button type="button" className="linklike"
                          title={t('copyInviteLinkHint')}
                          onClick={() => copyInvite(inv.token)}>
                          {copiedToken === inv.token ? t('copiedToClipboard') : t('copyInviteLink')}
                        </button>
                      ) : null}
                      {delivery === 'failed' ? (
                        <span className="dim">
                          {' '}⚠ {t('inviteFailed')}
                          {inv!.channel === 'email' ? (
                            <button type="button" className="linklike"
                              disabled={resend.isPending}
                              onClick={() => resend.mutate(inv!.id)}>
                              {t('inviteRetry')}
                            </button>
                          ) : null}
                        </span>
                      ) : null}
                      {delivery === 'opened' ? <span className="dim"> · {t('inviteOpened')}</span> : null}
                      {delivery === 'sent' ? <span className="dim"> · {t('inviteSent')}</span> : null}
                      {/* قرینه‌ی موبایل (WHOSUP-236): «از بیرون» یک جمله است و
                          جایش کنارِ اسم است، نه داخلِ نشانِ کوتاهِ سمتِ دیگر. */}
                      {external ? <span className="dim"> · {t('invitedExternal')}</span> : null}
                      {!inv && r.recipientId && r.response === 'pending' ? (
                        <span className="dim">
                          {' '}· {r.seenAt ? t('seenNotAnswered') : t('notSeenYet')}
                        </span>
                      ) : null}
                      {/* سازنده ببیند چه کسی را چه کسی آورده (مایگریشن 0132). */}
                      {r.via === 'chain' ? (
                        <span className="dim">
                          {' '}· {t('chainBroughtBy', {
                            name: (recipients.data ?? [])
                              .find((x) => x.recipientId === r.invitedBy)
                              ?.profile?.displayName ?? '',
                          })}
                        </span>
                      ) : null}
                    </span>
                    <span className={`count${r.response === 'in' ? ' ok' : ''}`}>
                      {responseChipLabel(r)}
                    </span>
                  </div>
                );
              })}
              {(linkViews.data ?? 0) > 0 ? (
                <p className="dim">{t('linkViews', { n: String(linkViews.data) })}</p>
              ) : null}
              {!plan.data && s.status === 'active' ? (
                finalWhen ? (
                  <div className="field">
                    <label>{t('confirmWhenTitle')}</label>
                    <div className="two-up">
                      <label className="field">
                        <span>{t('pickDate')}</span>
                        <input className="input" type="date" value={finalWhen.date}
                          onChange={(e) => setFinalWhen({ ...finalWhen, date: e.target.value })} />
                      </label>
                      <label className="field">
                        <span>{t('pickTime')}</span>
                        <input className="input" type="time" value={finalWhen.time}
                          onChange={(e) => setFinalWhen({ ...finalWhen, time: e.target.value })} />
                      </label>
                    </div>
                    <input className="input" value={finalPlace}
                      placeholder={t('placePlaceholder')}
                      onChange={(e) => setFinalPlace(e.target.value)} />
                    <p className="dim">{t('reminderLabel')}</p>
                    <div className="chiprow">
                      {REMINDER_OPTIONS.map((o) => (
                        <button key={o.min} type="button"
                          className={`chip${remind === o.min ? ' on' : ''}`}
                          onClick={() => setRemind(o.min)}>
                          {t(o.key)}
                        </button>
                      ))}
                    </div>
                    {confirmGaps.length ? (
                      <Note tone="warn">{confirmGaps.join(' · ')}</Note>
                    ) : null}
                    <button type="button" className="btn fill" disabled={confirm.isPending}
                      onClick={() => {
                        const gaps = confirmMissingAll({
                          hasDay: !!finalWhen.date,
                          hasClock: !!finalWhen.time,
                          place: finalPlace,
                        });
                        setConfirmGaps(gaps.map((g) => t(GAP_KEY[g])));
                        if (!gaps.length) confirm.mutate();
                      }}>
                      {confirm.isPending ? '…' : t('confirmPlan')}
                    </button>
                    <p className="dim">{t('confirmPlanHint')}</p>
                  </div>
                ) : (
                  <>
                    <button type="button" className="btn fill" onClick={openConfirm}>
                      {t('confirmPlan')}
                    </button>
                    <p className="dim">{t('confirmPlanHint')}</p>
                  </>
                )
              ) : null}
            </Card>
          ) : null}

          {plan.data ? (
            <Card className="confirmed">
              <h3>{t('planSummary')}</h3>
              <p>
                {formatWeekdayDate(plan.data.finalStartsAt)}
                {plan.data.finalPlace ? ` · ${plan.data.finalPlace}` : ''}
              </p>
              <p className="dim">{t('reminderNote', { h: plan.data.reminderMin / 60 })}</p>
              {rosterWorthShowing(roster.data) ? (
                <>
                  <h3>{t('planRosterTitle')}</h3>
                  {roster.data!.people.map((p) => (
                    <div key={p.id} className="tally-row">
                      <span>
                        {p.avatarEmoji} {p.displayName}
                        {p.isCreator ? <span className="dim"> {t('planRosterOrganizer')}</span> : null}
                      </span>
                    </div>
                  ))}
                  {roster.data!.guests > 0 ? (
                    <p className="dim">{t('planRosterGuests', { n: String(roster.data!.guests) })}</p>
                  ) : null}
                </>
              ) : null}
              {/*
               * «افزودن به تقویم» — لینکِ گوگل برای یک کلیک، و خودِ فایلِ
               * .ics برای اپل/اوت‌لوک. UID پایدار (plan-<id>) و SEQUENCE از
               * روی ساعتِ تولید، تا ویرایشِ زمان رویدادِ دوم نسازد
               * (core/lib/calendar.ts).
               */}
              {(() => {
                const ev = {
                  title: s.title, startsAt: plan.data.finalStartsAt,
                  placeName: plan.data.finalPlace, url: signalUrl(s.shareToken),
                };
                const cal = googleCalendarUrl(ev);
                const ics = buildIcs({
                  ...ev, uid: `plan-${s.id}`,
                  sequence: Math.floor(Date.now() / 60000), now: new Date(),
                });
                return (
                  <p>
                    {cal ? (
                      <a href={cal} target="_blank" rel="noreferrer">{t('addToCalendar')}</a>
                    ) : null}
                    {ics ? (
                      <>
                        {' · '}
                        <a download="plan.ics"
                          href={`data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`}>
                          .ics
                        </a>
                      </>
                    ) : null}
                  </p>
                );
              })()}
              {isCreator && plan.data.outcome === 'pending' ? (
                <>
                  <p className="dim">{t('logOutcomePrompt')}</p>
                  <div className="chiprow">
                    <button type="button" className="btn"
                      onClick={() => outcome.mutate({ o: 'done', share: 'none' })}>
                      {t('outcomeDone')}
                    </button>
                    <button type="button" className="btn"
                      onClick={() => outcome.mutate({ o: 'done', share: 'friends' })}>
                      {t('outcomeDoneShareBtn')}
                    </button>
                    <button type="button" className="btn"
                      onClick={() => outcome.mutate({ o: 'partial', share: 'none' })}>
                      {t('outcomePartial')}
                    </button>
                    <button type="button" className="btn"
                      onClick={() => outcome.mutate({ o: 'cancelled', share: 'none' })}>
                      {t('outcomeCancel')}
                    </button>
                  </div>
                </>
              ) : null}
              {plan.data.outcome === 'done' ? <p>{t('doneBadge')}</p> : null}
              {plan.data.outcome === 'partial' ? <p>{t('outcomePartial')}</p> : null}
            </Card>
          ) : null}
        </>
      ) : tab === 'photos' ? (
        // تا نفهمیم برنامه قطعی شده یا نه، «هنوز قطعی نشده» گفتن دروغِ
        // نیم‌ثانیه‌ایه که درست بعدش خودش رو نقض می‌کنه.
        plan.isLoading ? <p className="dim">…</p> : (
          <SignalPhotos signalId={s.id} hasPlan={!!plan.data} isCreator={isCreator}
            myResponse={s.myResponse} cancelled={cancelled} />
        )
      ) : (
        // گفتگو فقط برای سازنده و کسی که گفته «هستم» — همون قفلِ موبایل.
        !canChat ? (
          <Card><Note>{t('chatLockedNote')}</Note></Card>
        ) : (
          <Card>
            {(messages.data ?? []).length === 0 ? <p className="dim">{t('chatEmpty')}</p> : null}
            <div className="chat">
              {(messages.data ?? []).map((m) => (
                <div key={m.id} className={`bubble${m.senderId === profile?.id ? ' mine' : ''}`}>
                  {/* سرِ حباب: نامِ فرستنده، و پرچمِ گزارش در انتهای همان خط
                      (WHOSUP-168). گزارش نه روی پیامِ خودم است و نه روی پیامِ
                      سیستمی (`senderId` ندارد) — آن یکی را خودِ اپ نوشته. */}
                  {m.senderId !== profile?.id ? (
                    <div className="bubble-head">
                      <small className="dim">{m.senderName ?? t('system')}</small>
                      {m.senderId ? (
                        <ReportButton look="mini" target={{ kind: 'message', id: Number(m.id) }} />
                      ) : null}
                    </div>
                  ) : null}
                  <p>{m.body}</p>
                  {m.heldReason ? <HeldMessageNote m={m} /> : null}
                </div>
              ))}
            </div>
            <div className="menu-custom">
              <input className="input" placeholder={t('typeMessage')} value={msg}
                onChange={(e) => setMsg(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && msg.trim()) sendMsg.mutate(); }} />
              <button type="button" className="btn" disabled={!msg.trim() || sendMsg.isPending}
                onClick={() => sendMsg.mutate()}>{t('send')}</button>
            </div>
          </Card>
        )
      )}

      {/* قرارِ WHOSUP-71: بعد از ساختِ اولین سیگنال، کاربر همین‌جا فرود
          می‌آید (CreateSignalPage همان لحظه hasFirstActivity را باطل کرده)
          و پیشنهادِ اعلان باید همین‌جا باشد، نه در فیدی که شاید برنگردد. */}
      <PushOfferCard />
    </div>
  );
}
